import { ModuleRequestError } from './request-error.js';
/** Ошибка отмены пользователем — отличима от сбоя по `name`. */
export class UploadAbortedError extends Error {
    constructor() {
        super('upload_aborted');
        this.name = 'AbortError';
    }
}
/**
 * Ждать `promise`, пока не отменили: отмена (`signal`) — сразу
 * `UploadAbortedError`, не дожидаясь самого обещания (его результат
 * отбрасывается). Без `signal` — просто `promise`.
 */
export function untilAborted(promise, signal) {
    if (!signal)
        return promise;
    if (signal.aborted) {
        promise.catch(() => undefined);
        return Promise.reject(new UploadAbortedError());
    }
    return new Promise((resolve, reject) => {
        const onAbort = () => reject(new UploadAbortedError());
        signal.addEventListener('abort', onAbort, { once: true });
        promise.then((v) => {
            signal.removeEventListener('abort', onAbort);
            resolve(v);
        }, (err) => {
            signal.removeEventListener('abort', onAbort);
            reject(err);
        });
    });
}
/** Пауза, которую прерывает отмена: `UploadAbortedError` сразу, без ожидания таймера. */
export function abortableSleep(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new UploadAbortedError());
            return;
        }
        const onAbort = () => {
            clearTimeout(timer);
            reject(new UploadAbortedError());
        };
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}
/**
 * План не сходится с файлом: части не покроют файл ровно (`partCount !==
 * ceil(size / partSize)`), ссылок не столько, сколько частей, или размер
 * части не тот. Код — `upload_plan_mismatch`, статус 0 (не HTTP-ответ).
 */
export function uploadPlanError(plan, size) {
    const { partSize, partCount, parts } = plan;
    const bad = (why) => new ModuleRequestError('upload_plan_mismatch', 0, {
        serverMessage: `Сервер предложил неверный план загрузки: ${why}. Загрузите файл заново.`,
    });
    if (!Number.isSafeInteger(partSize) || partSize <= 0)
        return bad(`размер части ${partSize}`);
    const expected = Math.ceil(size / partSize);
    if (!Number.isSafeInteger(partCount) || partCount !== expected) {
        return bad(`частей ${partCount}, а файлу нужно ${expected}`);
    }
    if (parts.length !== partCount)
        return bad(`ссылок ${parts.length}, частей ${partCount}`);
    const seen = new Set();
    for (const p of parts) {
        const n = p.partNumber;
        if (!Number.isSafeInteger(n) || n < 1 || n > partCount || seen.has(n)) {
            return bad(`номер части ${n}`);
        }
        seen.add(n);
        const want = Math.min(partSize, size - (n - 1) * partSize);
        if (p.size !== want)
            return bad(`часть ${n} — ${p.size} байт, нужно ${want}`);
    }
    return null;
}
const defaultDelay = (attempt) => Math.min(1000 * 2 ** (attempt - 1), 8000);
/** Повторяемо ли: сеть (status 0), 408, 429, 5xx. Прочие 4xx — нет (403 — обновить ссылку). */
export function isRetryablePartStatus(status) {
    return status === 0 || status === 408 || status === 429 || status >= 500;
}
/** Ссылка истекла / подпись не принята — S3 отвечает 403: взять свежую ссылку и повторить. */
const isExpiredUrlStatus = (status) => status === 403;
/** Сколько раз одна часть может получить свежую ссылку. */
const MAX_REFRESHES_PER_PART = 2;
/** Код ошибки S3 из XML-тела (`<Code>AccessDenied</Code>`); не XML — null. */
function storageCodeOf(text) {
    const m = /<Code>([A-Za-z0-9]{1,64})<\/Code>/.exec(text);
    return m ? m[1] : null;
}
function storageError(status, text) {
    if (status === 0) {
        return new ModuleRequestError('network_error', 0, {
            serverMessage: 'Нет связи с хранилищем файлов — проверьте интернет и загрузите файл ещё раз.',
        });
    }
    const code = storageCodeOf(text);
    return new ModuleRequestError('storage_rejected', status, {
        upstreamCode: code,
        serverMessage: `Хранилище файлов отклонило часть файла (HTTP ${status}${code ? `, ${code}` : ''}). Загрузите файл ещё раз.`,
    });
}
/** Сторож простоя PUT части по умолчанию: 2 мин без прогресса — сбой. */
export const DEFAULT_PART_IDLE_TIMEOUT_MS = 2 * 60_000;
/** Сторож ответа после отправки всех байт части по умолчанию: 60 с. */
export const DEFAULT_PART_RESPONSE_TIMEOUT_MS = 60_000;
/**
 * Одна попытка PUT части. Каждая попытка — свой XHR и свои сторожа (простоя
 * отправки и ответа): повтор зависшей части тоже оборвётся по таймауту.
 *
 * Сторож и отмена завершают попытку САМИ, не дожидаясь `onabort`: у
 * повисшего запроса браузер (или перехват сети) может не прислать событие
 * отмены — тогда попытка висела бы вечно (стенд v6, V2). Поздние события
 * брошенного XHR после этого игнорируются.
 */
function sendPart(makeXhr, url, body, signal, idleMs, responseMs, onLoaded) {
    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(new UploadAbortedError());
            return;
        }
        const xhr = makeXhr();
        let settled = false;
        let timer;
        const finish = (outcome) => {
            if (settled)
                return false;
            settled = true;
            if (timer !== undefined)
                clearTimeout(timer);
            signal.removeEventListener('abort', onAbortSignal);
            if (outcome instanceof UploadAbortedError)
                reject(outcome);
            else
                resolve(outcome);
            return true;
        };
        const dropXhr = () => {
            try {
                xhr.abort();
            }
            catch {
                // брошенный запрос: его судьба попытке уже не важна
            }
        };
        const failed = (stalled) => finish({ ok: false, status: 0, text: '', etag: null, stalled });
        // Пока байты уходят — сторож простоя отправки; когда ушли все — сторож
        // ответа: дальше прогресса не будет, ждём только ответ хранилища.
        const arm = (ms) => {
            if (settled)
                return;
            if (timer !== undefined)
                clearTimeout(timer);
            timer = setTimeout(() => {
                // обрыв сторожем — не отмена пользователем, а сбой попытки (повтор)
                if (failed(true))
                    dropXhr();
            }, ms);
        };
        function onAbortSignal() {
            if (finish(new UploadAbortedError()))
                dropXhr();
        }
        signal.addEventListener('abort', onAbortSignal, { once: true });
        xhr.open('PUT', url);
        // Подписанная ссылка — сама себе авторизация: куки админки хранилищу не
        // нужны (и CORS с credentials потребовал бы точный Origin в бакете).
        xhr.withCredentials = false;
        xhr.upload.onprogress = (e) => {
            if (settled)
                return;
            const sentAll = e.loaded >= (e.total && e.total > 0 ? e.total : body.size);
            arm(sentAll ? responseMs : idleMs);
            onLoaded(e.loaded);
        };
        xhr.onload = () => {
            const ok = xhr.status >= 200 && xhr.status < 300;
            finish({
                ok,
                status: xhr.status,
                text: ok ? '' : xhr.responseText,
                etag: ok ? xhr.getResponseHeader('ETag') : null,
                stalled: false,
            });
        };
        xhr.onerror = () => failed(false);
        xhr.ontimeout = () => failed(false);
        // Отмену сторожем и сигналом попытка уже учла; иное `abort` (браузер
        // оборвал запрос сам) — сетевой сбой, а не отмена пользователем.
        xhr.onabort = () => {
            if (signal.aborted)
                finish(new UploadAbortedError());
            else
                failed(false);
        };
        arm(idleMs);
        xhr.send(body);
    });
}
/** Часть так и не ответила ни на одной попытке — сторож обрывал каждую. */
function stalledPartError(partNumber, attempts) {
    return new ModuleRequestError('upload_part_stalled', 0, {
        serverMessage: `Хранилище файлов не ответило на часть ${partNumber} (попыток: ${attempts}). Проверьте интернет и нажмите «Повторить».`,
    });
}
/**
 * Загрузить файл частями прямо в хранилище и собрать его на сервере.
 *
 * - До первой части план сверяется с файлом (`uploadPlanError`): неверный —
 *   `ModuleRequestError('upload_plan_mismatch')` и `abort`, ни байта не ушло.
 * - Части идут параллельно (`concurrency`, по умолчанию 3). Повтор части в
 *   S3 идемпотентен (тот же номер перезаписывается), поэтому ретрай безопасен:
 *   сеть / 408 / 429 / 5xx — до `retries` раз с паузой.
 * - 403 — истёкшая подпись: свежие ссылки одним запросом (`refreshUrls`) на
 *   все незагруженные части и повтор без паузы (до 2 раз на часть).
 * - 2xx без `ETag` — `upload_etag_missing` (бакет не отдаёт заголовок: CORS
 *   `ExposeHeaders: ETag`), без повторов — каждый повтор кончился бы тем же.
 * - Отмена (`signal`) обрывает идущие части и паузы, зовёт `abort` и бросает
 *   `UploadAbortedError` (`name === 'AbortError'`) — и когда отменили ещё до
 *   вызова (пока открывалась сессия); окончательный сбой части —
 *   `ModuleRequestError` + `abort` на сервере.
 * - Сбой `complete` загрузку НЕ отменяет: все части в хранилище, повтор
 *   решает вызывающий.
 * - Отмена во время `complete` (сборка или опрос «ещё собирается») —
 *   ожидание обрывается сразу, на сервере зовётся `abort` (best-effort), и
 *   бросается `UploadAbortedError`: кнопка «Отмена» работает до конца.
 */
export async function uploadInParts(opts) {
    const { file, plan, signal, onProgress, retries = 3, retryDelayMs = defaultDelay, sleep = abortableSleep, partIdleTimeoutMs = DEFAULT_PART_IDLE_TIMEOUT_MS, partResponseTimeoutMs = DEFAULT_PART_RESPONSE_TIMEOUT_MS, } = opts;
    // Пакет собирается без DOM-lib: конструктор берётся из globalThis.
    const makeXhr = opts.xhrFactory ??
        (() => new globalThis.XMLHttpRequest());
    // Свой контроллер: первая окончательная ошибка части обрывает остальные.
    const ctrl = new AbortController();
    const onUserAbort = () => ctrl.abort();
    signal?.addEventListener('abort', onUserAbort, { once: true });
    const total = file.size;
    const urls = new Map(plan.parts.map((p) => [p.partNumber, p.url]));
    const loaded = new Map();
    const etags = new Map();
    const retrying = new Set();
    const report = () => {
        let sum = 0;
        for (const v of loaded.values())
            sum += v;
        onProgress?.({
            loaded: Math.min(total, sum),
            total,
            partsDone: etags.size,
            partCount: plan.partCount,
            retryingParts: [...retrying].sort((a, b) => a - b),
        });
    };
    let refreshing = null;
    const refresh = () => {
        if (refreshing)
            return refreshing;
        const pending = [];
        for (let n = 1; n <= plan.partCount; n += 1)
            if (!etags.has(n))
                pending.push(n);
        refreshing = opts
            .refreshUrls(pending)
            .then((fresh) => {
            for (const p of fresh)
                urls.set(p.partNumber, p.url);
        })
            .finally(() => {
            refreshing = null;
        });
        return refreshing;
    };
    const uploadPart = async (n) => {
        const start = (n - 1) * plan.partSize;
        const body = file.slice(start, Math.min(total, start + plan.partSize));
        let attempt = 0;
        let refreshes = 0;
        for (;;) {
            const url = urls.get(n);
            if (url === undefined)
                throw storageError(0, '');
            const r = await sendPart(makeXhr, url, body, ctrl.signal, partIdleTimeoutMs, partResponseTimeoutMs, (b) => {
                loaded.set(n, b);
                report();
            });
            if (r.ok) {
                if (!r.etag) {
                    throw new ModuleRequestError('upload_etag_missing', r.status, {
                        serverMessage: 'Хранилище не отдало ETag части файла — у бакета не настроен CORS (ExposeHeaders: ETag). Обратитесь к администратору.',
                    });
                }
                etags.set(n, r.etag);
                loaded.set(n, body.size);
                retrying.delete(n);
                report();
                return;
            }
            loaded.set(n, 0);
            const willRetry = (isExpiredUrlStatus(r.status) && refreshes < MAX_REFRESHES_PER_PART) ||
                (isRetryablePartStatus(r.status) && attempt < retries);
            if (willRetry)
                retrying.add(n);
            else
                retrying.delete(n);
            report();
            if (isExpiredUrlStatus(r.status) && refreshes < MAX_REFRESHES_PER_PART) {
                refreshes += 1;
                await refresh();
                continue;
            }
            if (!isRetryablePartStatus(r.status) || attempt >= retries) {
                throw r.stalled ? stalledPartError(n, attempt + 1) : storageError(r.status, r.text);
            }
            attempt += 1;
            await sleep(retryDelayMs(attempt), ctrl.signal);
            if (ctrl.signal.aborted)
                throw new UploadAbortedError();
        }
    };
    const cleanup = async () => {
        if (!opts.abort)
            return;
        try {
            await opts.abort();
        }
        catch {
            // best-effort: незавершённую загрузку уберёт lifecycle бакета (1 день)
        }
    };
    try {
        // Отменили, пока открывалась сессия (`plan` уже выдан сервером): бросок
        // внутри `try` — сессия закрывается через `abort`, а не висит до lifecycle.
        if (signal?.aborted)
            throw new UploadAbortedError();
        const planError = uploadPlanError(plan, total);
        if (planError)
            throw planError;
        report();
        let next = 1;
        let firstError = null;
        const worker = async () => {
            while (firstError === null && next <= plan.partCount) {
                const n = next;
                next += 1;
                try {
                    await uploadPart(n);
                }
                catch (err) {
                    if (firstError === null)
                        firstError = err;
                    ctrl.abort();
                }
            }
        };
        const width = Math.max(1, Math.min(opts.concurrency ?? 3, plan.partCount));
        await Promise.all(Array.from({ length: width }, worker));
        // отмена пользователем важнее «отмены» соседних частей, которую она вызвала
        if (signal?.aborted)
            throw new UploadAbortedError();
        if (firstError !== null)
            throw firstError;
    }
    catch (err) {
        await cleanup();
        throw err;
    }
    finally {
        signal?.removeEventListener('abort', onUserAbort);
    }
    const parts = [...etags.entries()]
        .sort(([a], [b]) => a - b)
        .map(([partNumber, etag]) => ({ partNumber, etag }));
    try {
        return await untilAborted(opts.complete(parts, signal), signal);
    }
    catch (err) {
        // отмена на стадии сборки: сессию закрываем, а не ждём lifecycle бакета
        if (signal?.aborted) {
            await cleanup();
            throw new UploadAbortedError();
        }
        throw err;
    }
}
//# sourceMappingURL=upload-in-parts.js.map